// AstriaGCCV2Card.tsx
import React from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SHELL_COLORS_GCC_V2 } from "./AstriaGCCV2Shell";
import { GCCV2Language } from "../../../utils/GCC/astriaGCCV2Language";
import { LC } from "../../../utils/lnText";

// ─── Astria GCC v2 — premium card primitives ───────────────────────────────
// Separate from src/components/astro/shared/SectionCard.tsx (flat, no
// shadow/border, shared by 8+ other lanes) — GCC v2's luxury brief calls
// for soft shadows and elegant gold-tinted borders instead.

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

// The AI response body is plain markdown (bold, bullets, dividers), not
// plain text — the prompts never forbid that formatting, only the section
// headings are pinned. Rendering it as inline styles here (rather than
// dumping raw text into a <p>) keeps bold/bullets/`---` dividers looking
// intentional instead of showing literal `**`/`*`/`---` characters.
const GCCV2CardMarkdown: React.FC<{ text: string; color: string }> = ({
  text,
  color,
}) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ node, ...props }) => (
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color,
            margin: "0 0 10px 0",
          }}
          {...props}
        />
      ),
      strong: ({ node, ...props }) => (
        <strong style={{ fontWeight: 700 }} {...props} />
      ),
      em: ({ node, ...props }) => (
        <em style={{ fontStyle: "italic" }} {...props} />
      ),
      ul: ({ node, ...props }) => (
        <ul
          style={{
            margin: "0 0 10px 0",
            paddingInlineStart: 20,
            listStyleType: "disc",
          }}
          {...props}
        />
      ),
      ol: ({ node, ...props }) => (
        <ol
          style={{
            margin: "0 0 10px 0",
            paddingInlineStart: 20,
            listStyleType: "decimal",
          }}
          {...props}
        />
      ),
      li: ({ node, ...props }) => (
        <li
          style={{ fontSize: 15, lineHeight: 1.75, color, marginBottom: 4 }}
          {...props}
        />
      ),
      hr: () => (
        <hr
          style={{
            border: "none",
            borderTop: `1px solid ${SHELL_COLORS_GCC_V2.cardBorder}`,
            margin: "14px 0",
          }}
        />
      ),
    }}
  >
    {text}
  </ReactMarkdown>
);

interface GCCV2ResponseContainerProps {
  children: React.ReactNode;
}

export const GCCV2ResponseContainer: React.FC<GCCV2ResponseContainerProps> = ({
  children,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 20,
      width: "100%",
    }}
  >
    {children}
  </div>
);

interface GCCV2ProgressBarProps {
  value: number; // 0-100
  label?: string;
}

export const GCCV2ProgressBar: React.FC<GCCV2ProgressBarProps> = ({
  value,
  label,
}) => {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div style={{ marginTop: 12 }}>
      {label && (
        <div
          style={{
            fontSize: 11,
            color: SHELL_COLORS_GCC_V2.mutedColor,
            marginBottom: 6,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
      )}
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: SHELL_COLORS_GCC_V2.section,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${clamped}%`,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${SHELL_COLORS_GCC_V2.accent}, #E3C77A)`,
            transition: "width 0.6s ease",
          }}
        />
      </div>
    </div>
  );
};

interface GCCV2Props {
  header: string;
  mainInsight: string;
  supportingExplanation?: string;
  microText?: string;
  progressBar?: { value: number; label?: string };
  companionNote?: string;
  delay?: number;
  fontFamily?: string;
  alt?: boolean;
}

export const GCCV2Card: React.FC<GCCV2Props> = ({
  header,
  mainInsight,
  supportingExplanation,
  microText,
  progressBar,
  companionNote,
  delay = 0,
  fontFamily,
  alt = false,
}) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <motion.div
      custom={delay}
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: alt
          ? SHELL_COLORS_GCC_V2.section
          : SHELL_COLORS_GCC_V2.card,
        border: `1px solid ${SHELL_COLORS_GCC_V2.cardBorder}`,
        borderRadius: 16,
        padding: 24,
        boxShadow: hovered
          ? "0 6px 24px rgba(43,39,37,0.09), 0 0 0 1px rgba(201,162,75,0.25)"
          : "0 4px 20px rgba(43,39,37,0.06)",
        transition: "box-shadow 0.3s ease",
        fontFamily,
      }}
    >
      {microText && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: SHELL_COLORS_GCC_V2.accent,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          {microText}
        </div>
      )}
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: SHELL_COLORS_GCC_V2.sectionTitleColor,
          marginBottom: 10,
        }}
      >
        {header}
      </div>
      <GCCV2CardMarkdown
        text={mainInsight}
        color={SHELL_COLORS_GCC_V2.bodyColor}
      />
      {supportingExplanation && (
        <p
          style={{
            fontSize: 13.5,
            lineHeight: 1.7,
            color: SHELL_COLORS_GCC_V2.mutedColor,
            marginTop: 10,
            whiteSpace: "pre-wrap",
          }}
        >
          {supportingExplanation}
        </p>
      )}
      {progressBar && (
        <GCCV2ProgressBar value={progressBar.value} label={progressBar.label} />
      )}
      {companionNote && (
        <div
          style={{
            marginTop: 16,
            paddingInlineStart: 14,
            borderInlineStart: `2px solid ${SHELL_COLORS_GCC_V2.accent}`,
            fontSize: 13.5,
            fontStyle: "italic",
            color: SHELL_COLORS_GCC_V2.bodyColor,
            lineHeight: 1.6,
          }}
        >
          {companionNote}
        </div>
      )}
    </motion.div>
  );
};

interface GCCV2PlainTextCardProps {
  header: string;
  text: string;
  delay?: number;
  fontFamily?: string;
}

export const GCCV2PlainTextCard: React.FC<GCCV2PlainTextCardProps> = ({
  header,
  text,
  delay = 0,
  fontFamily,
}) => (
  <motion.div
    custom={delay}
    initial="hidden"
    animate="visible"
    variants={fadeUp}
    style={{
      background: SHELL_COLORS_GCC_V2.card,
      border: `1px solid ${SHELL_COLORS_GCC_V2.cardBorder}`,
      borderRadius: 16,
      padding: 24,
      boxShadow: "0 4px 20px rgba(43,39,37,0.06)",
      fontFamily,
    }}
  >
    <div
      style={{
        fontSize: 18,
        fontWeight: 600,
        color: SHELL_COLORS_GCC_V2.sectionTitleColor,
        marginBottom: 10,
      }}
    >
      {header}
    </div>
    <GCCV2CardMarkdown text={text} color={SHELL_COLORS_GCC_V2.bodyColor} />
  </motion.div>
);

export const gccV2LoadingSpinner = (
  accent: string = SHELL_COLORS_GCC_V2.accent,
) => (
  <div
    style={{
      width: 28,
      height: 28,
      border: `3px solid ${SHELL_COLORS_GCC_V2.section}`,
      borderTopColor: accent,
      borderRadius: "50%",
    }}
    className="animate-spin"
  />
);

export const gccV2SectionKindLabel = (
  kind: string,
  language: GCCV2Language,
): string => {
  const labels: Record<string, { en: string; ar: string }> = {
    acknowledgement: { en: "A Gentle Welcome", ar: "ترحيب هادئ" },
    understanding: { en: "Emotional Understanding", ar: "فهم عاطفي" },
    rhythm: { en: "Your Personal Rhythm", ar: "إيقاعك الشخصي" },
    guidance: { en: "Calm Guidance", ar: "إرشاد هادئ" },
    closing: { en: "A Quiet Word", ar: "كلمة أخيرة" },
    generic: { en: "Reflection", ar: "تأمل" },
  };
  return LC((labels[kind] || labels.generic), language);
};
